import type { ConceptProof, ConceptVisualDirection } from '@/lib/briefs/concept-contract';

export interface DesignPromptConcept {
  visualDirection: ConceptVisualDirection;
  proof: ConceptProof;
  hook: string | null;
  rationale: string | null;
}

export type DesignPromptSource = 'subject' | 'proof' | 'headline' | 'rationale';

/**
 * Deliberately not a discriminated union: this repo compiles with
 * `strict: false`, where `ok`-narrowing does not happen and every consumer
 * would fail to read `reason`. Exactly one of (`prompt`, `source`) or
 * `reason` is non-null.
 */
export interface DesignPromptGate {
  ok: boolean;
  prompt: string | null;
  source: DesignPromptSource | null;
  reason: string | null;
}

function allowed(prompt: string, source: DesignPromptSource): DesignPromptGate {
  return { ok: true, prompt, source, reason: null };
}

function refused(reason: string): DesignPromptGate {
  return { ok: false, prompt: null, source: null, reason };
}

export const NOTHING_TO_GENERATE_FROM_REASON =
  'This concept has no subject, proof quote, on-image headline, or hook to build artwork from. Add one in Brief details.';

/**
 * What "Generate this size" sends when the Concept never authored an explicit
 * `visual_direction.subject`. A Concept made by the Brief pipeline usually
 * carries a sourced proof quote, an on-image headline and a hook even when
 * the subject field is blank, so a blank subject is not "nothing to generate
 * from". The order is most concrete fact first: authored subject, then the
 * sourced proof quote, then the on-image headline or hook, then the rationale.
 *
 * A `refusal` on the visual direction outranks all of it: the pipeline
 * decided no honest image can be produced (a model-rendered screenshot or
 * chart is fabricated by construction), so deriving a prompt anyway would
 * override that decision. The gate names the refusal instead.
 */
export function resolveDesignPromptGate(concept: DesignPromptConcept | null): DesignPromptGate {
  if (!concept) return refused(NOTHING_TO_GENERATE_FROM_REASON);
  const direction = concept.visualDirection;
  const refusal = direction.refusal?.trim();
  if (refusal) {
    return refused(`This concept needs a real capture, not a generated image: ${refusal}`);
  }
  const subject = direction.subject?.trim();
  if (subject) {
    const setting = direction.setting?.trim();
    return allowed(setting ? `${subject}. ${setting}` : subject, 'subject');
  }
  const proofQuote = concept.proof.kind !== 'none' ? concept.proof.quote?.trim() : null;
  if (proofQuote) {
    return allowed(`A typographic card presenting this sourced fact: "${proofQuote}"`, 'proof');
  }
  const headline = direction.textPlan?.headline?.trim() || concept.hook?.trim();
  if (headline) {
    return allowed(`A typographic card with the headline: "${headline}"`, 'headline');
  }
  const rationale = concept.rationale?.trim();
  if (rationale) return allowed(rationale, 'rationale');
  return refused(NOTHING_TO_GENERATE_FROM_REASON);
}

export interface SiblingDesignCandidate {
  id: string;
  aspectRatio: string;
  mediaId: string | null;
  isGenerating: boolean;
  followsSetImage: boolean;
  version: number;
  position: number;
}

export interface SiblingDesignReference {
  variantId: string;
  aspectRatio: string;
  imageUrl: string;
  flat: boolean;
}

/**
 * A Concept whose recorded visual intent is `none` and which names no photographic subject is a
 * typographic / flat-design card: its image is text, colour and a logo on a plain field. Nothing
 * outside the frame is worth painting, so a new size must be re-laid from the same spec, never
 * outpainted (outpainting a text card invents scenery). Read from the Concept's own visual
 * direction, not guessed from pixels.
 */
export function isFlatDesignDirection(direction: unknown): boolean {
  if (!direction || typeof direction !== 'object') return false;
  const { intent, subject, refusal } = direction as {
    intent?: unknown;
    subject?: unknown;
    refusal?: unknown;
  };
  if (typeof refusal === 'string' && refusal.trim()) return false;
  const hasSubject = typeof subject === 'string' && subject.trim().length > 0;
  return intent === 'none' && !hasSubject;
}

export function recomposeFlatDesignInstruction(targetAspectRatio: string): string {
  return `Re-lay the attached typographic card as a ${targetAspectRatio} card. This is a graphic design, not a photograph: no photography, no scene, no desk and no objects. Keep the same colours, backdrop treatment, typeface and hierarchy, the same logo in the same corner and the same alignment, re-flowed to fill the ${targetAspectRatio} canvas. Re-set the exact same text; do not add, remove or reword any of it.`;
}

export type GenerateSizePlan =
  | { kind: 'card-spec'; prompt: string }
  | { kind: 'sibling'; reference: SiblingDesignReference }
  | { kind: 'direction'; prompt: string }
  | { kind: 'none' };

/**
 * What "Generate this size" does. A typographic (flat) Concept is rendered FROM ITS CARD SPEC at the
 * new ratio, exactly like its first size: the request carries no reference image and no
 * re-lay instruction, so the server resolves the same card (headline, numeral split, motif, palette,
 * fonts, source line, composited logo). Re-laying a sibling's pixels carried an old card's look into
 * every new size (server/api/generate-image.post.ts only builds a card when the request has no
 * reference). Only a flat Concept with nothing to build a card from falls back to the sibling.
 */
export function planGenerateThisSize(input: {
  gate: DesignPromptGate;
  sibling: SiblingDesignReference | null;
}): GenerateSizePlan {
  if (input.sibling?.flat && input.gate.ok && input.gate.prompt) {
    return { kind: 'card-spec', prompt: input.gate.prompt };
  }
  if (input.sibling) return { kind: 'sibling', reference: input.sibling };
  if (input.gate.ok && input.gate.prompt) return { kind: 'direction', prompt: input.gate.prompt };
  return { kind: 'none' };
}

/**
 * The size whose CURRENT image "Generate this size" should carry forward, when the Concept already
 * has one. The Concept's stored `visual_direction` is the plan the first image was made from; once a
 * size has been edited by prompt it is stale, and regenerating from it rebuilt the old card (ITC Tax
 * Concept 1: a water-survey card came back after the 1.91:1 had been rewritten to property tax).
 * A size that owns its image (`followsSetImage` false: edited, outpainted or reframed) wins over one
 * that merely follows the set image; the most recently saved (`version`), then the first in order,
 * breaks ties. A refusal on the visual direction still stops all of it, so this never turns a
 * "needs a real capture" Concept into a generated image.
 */
export function resolveSiblingDesignReference(input: {
  concept: DesignPromptConcept | null;
  selectedVariantId: string | null;
  variants: readonly SiblingDesignCandidate[];
  mediaUrls: Readonly<Record<string, string | undefined>>;
}): SiblingDesignReference | null {
  if (input.concept?.visualDirection.refusal?.trim()) return null;
  const candidates = input.variants
    .filter(
      (variant) =>
        variant.id !== input.selectedVariantId &&
        !variant.isGenerating &&
        variant.mediaId &&
        input.mediaUrls[variant.mediaId],
    )
    .sort(
      (a, b) =>
        Number(a.followsSetImage) - Number(b.followsSetImage) ||
        b.version - a.version ||
        a.position - b.position,
    );
  const best = candidates[0];
  if (!best?.mediaId) return null;
  return {
    variantId: best.id,
    aspectRatio: best.aspectRatio,
    imageUrl: input.mediaUrls[best.mediaId]!,
    flat: isFlatDesignDirection(input.concept?.visualDirection),
  };
}

/**
 * What "Generate this size" sends when it carries a sibling size forward. The reference image, not
 * the Concept's original visual direction, is the authority for what the artwork shows and says.
 */
export function siblingDesignInstruction(
  reference: SiblingDesignReference,
  targetAspectRatio?: string,
): string {
  if (reference.flat && targetAspectRatio) return recomposeFlatDesignInstruction(targetAspectRatio);
  return `Recompose the attached ${reference.aspectRatio} design for this size. Keep its subject, headline, wording, colours and layout exactly as shown; the attached image is the current approved design and overrides any earlier artwork or visual direction. Do not add, remove or reword any text.`;
}
